import { useMemo } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { getScheduleReclassExcludedAccountTypes } from "@common/constants/domains/accounting/scheduleAccountFilters";
import { displayAccountLabel } from "@common/helpers/accounting";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { ScheduleOptionCards } from "../options/ScheduleOptionCards";
import {
    SCHEDULE_STOP_ACCOUNT_LABEL,
    SCHEDULE_STOP_ACCOUNT_PLACEHOLDER,
    SCHEDULE_STOP_RECLASS_ONE_ENTITY_HINT,
    ScheduleStopChoice,
    ScheduleStopSummary,
    buildScheduleStopOptionsCopy,
} from "./scheduleStopSettlementCopy";
import styles from "./ScheduleStopOptions.module.scss";

interface ScheduleStopOptionsProps {
    summary: ScheduleStopSummary;
    entityId: number | null;
    choice: ScheduleStopChoice | null;
    onChoiceChange: (choice: ScheduleStopChoice) => void;
    reclassAccount: Account | null;
    onReclassAccountChange: (account: Account | null) => void;
}

export const ScheduleStopOptions = ({
    summary,
    entityId,
    choice,
    onChoiceChange,
    reclassAccount,
    onReclassAccountChange,
}: ScheduleStopOptionsProps) => {
    const options = useMemo(
        () =>
            buildScheduleStopOptionsCopy(summary).map((option) => {
                const isReclassWithoutEntity =
                    option.choice === ScheduleStopChoice.reclass &&
                    entityId === null;
                return {
                    value: option.choice,
                    title: option.title,
                    description: isReclassWithoutEntity
                        ? `${option.description} ${SCHEDULE_STOP_RECLASS_ONE_ENTITY_HINT}`
                        : option.description,
                    isDisabled:
                        isReclassWithoutEntity ||
                        ((summary.remainingAfterStopAmount ?? 0) <= 0 &&
                            option.choice !== ScheduleStopChoice.decideLater),
                };
            }),
        [entityId, summary],
    );
    const reclassAccountTypes = useMemo(() => {
        const excludedTypes = getScheduleReclassExcludedAccountTypes({
            type: summary.scheduleType,
            revenueSubMode: summary.revenueSubMode,
            expenseSubMode: summary.expenseSubMode,
        });
        return Object.values(AccountType).filter(
            (type) => !excludedTypes.includes(type),
        );
    }, [summary.scheduleType, summary.revenueSubMode, summary.expenseSubMode]);

    return (
        <ScheduleOptionCards
            ariaLabel="Remaining balance"
            options={options}
            value={choice}
            onChange={onChoiceChange}
            renderSelectedExtra={(selected) =>
                selected === ScheduleStopChoice.reclass && entityId !== null ? (
                    <VStack gap={4}>
                        <span className={styles.accountLabel}>
                            {SCHEDULE_STOP_ACCOUNT_LABEL}
                        </span>
                        <AccountCombobox
                            entityId={entityId}
                            ledgerBasis={AccountingLedgerBasis.accruals}
                            allowedAccountTypes={reclassAccountTypes}
                            addNewAccountButton={false}
                            selectedAccount={reclassAccount ?? undefined}
                            onChange={onReclassAccountChange}
                            trigger={
                                <Picker.ButtonTrigger
                                    value={
                                        reclassAccount
                                            ? displayAccountLabel(
                                                  reclassAccount,
                                                  false,
                                              )
                                            : undefined
                                    }
                                    placeholder={
                                        SCHEDULE_STOP_ACCOUNT_PLACEHOLDER
                                    }
                                />
                            }
                        />
                    </VStack>
                ) : null
            }
        />
    );
};
